import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Snackbar from '@material-ui/core/Snackbar';
import SnackbarContent from '@material-ui/core/SnackbarContent';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';

import ErrorIcon from '../svg/ErrorIcon';
import Success from '../svg/Success';
import Warn from '../svg/Warn';
import InfoFilled from '../svg/InfoFilled';

export const useSuccessStyles = makeStyles((theme) => ({
  container: {
    backgroundColor: theme.palette.success.lighter,
    border: '1px solid',
    borderColor: theme.palette.success.light,
  },
  content: {
    display: 'flex',
    fontWeight: 500,
    color: `${theme.palette.success.main} !important`,
  },
  icon: {
    color: theme.palette.success.main,
    marginRight: theme.spacing(1),
    height: '18px !important',
    width: '18px !important',
  },
  close: {
    padding: theme.spacing(0.5),
  },
}));

export const useErrorStyles = makeStyles((theme) => ({
  container: {
    backgroundColor: theme.palette.error.lighter,
    border: '1px solid',
    borderColor: theme.palette.error.main,
  },
  content: {
    display: 'flex',
    fontWeight: 500,
    color: `${theme.palette.error.main} !important`,
  },
  icon: {
    color: theme.palette.error.main,
    marginRight: theme.spacing(1),
  },
  close: {
    padding: theme.spacing(0.5),
  },
}));

export const useWarningStyles = makeStyles((theme) => ({
  container: {
    backgroundColor: theme.palette.warning.lighter,
    border: '1px solid',
    borderColor: theme.palette.warning.light,
  },
  content: {
    display: 'flex',
    fontWeight: 500,
    color: `${theme.palette.warning.main} !important`,
  },
  icon: {
    color: theme.palette.warning.main,
    marginRight: theme.spacing(1),
    height: '18px !important',
    width: '18px !important',
  },
  close: {
    padding: theme.spacing(0.5),
  },
}));

export const useInfoStyles = makeStyles((theme) => ({
  container: {
    backgroundColor: theme.palette.info.lighter,
    border: '1px solid',
    borderColor: theme.palette.info.light,
  },
  content: {
    display: 'flex',
    fontWeight: 500,
    color: `${theme.palette.info.main} !important`,
  },
  icon: {
    color: theme.palette.info.main,
    marginRight: theme.spacing(1),
    height: '18px !important',
    width: '18px !important',
  },
  close: {
    padding: theme.spacing(0.5),
  },
}));

const SnackbarAlert = ({
  message,
  variant,
  autoHideDuration,
  allowClose,
  horizontalPosition,
  verticalPosition,
}) => {
  const errorClasses = useErrorStyles();
  const successClasses = useSuccessStyles();
  const warningClasses = useWarningStyles();
  const infoClasses = useInfoStyles();
  const [open, setOpen] = React.useState(message !== null && message !== undefined);
  let classes;
  let Icon;

  if (variant === 'error') {
    classes = errorClasses;
    Icon = ErrorIcon;
  } else if (variant === 'success') {
    classes = successClasses;
    Icon = Success;
  } else if (variant === 'warning') {
    classes = warningClasses;
    Icon = Warn;
  } else if (variant === 'info') {
    classes = infoClasses;
    Icon = InfoFilled;
  } else {
    return null;
  }

  const handleClose = (_e, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    setOpen(false);
  };

  return (
    <Snackbar
      anchorOrigin={{
        vertical: verticalPosition,
        horizontal: horizontalPosition,
      }}
      open={open}
      autoHideDuration={autoHideDuration}
      onClose={handleClose}
    >
      <SnackbarContent
        className={classes.container}
        aria-describedby="client-snackbar"
        message={
          <span id="client-snackbar" className={classes.content}>
            <Icon className={classes.icon} />
            {message}
          </span>
        }
        action={
          allowClose
            ? [
                <IconButton key="close" aria-label="close" color="inherit" onClick={handleClose}>
                  <CloseIcon className={classes.icon} />
                </IconButton>,
              ]
            : null
        }
      />
    </Snackbar>
  );
};

SnackbarAlert.propTypes = {
  message: PropTypes.string.isRequired,
  variant: PropTypes.oneOf(['success', 'error', 'warning', 'info']),
  autoHideDuration: PropTypes.number, // milliseconds
  allowClose: PropTypes.bool,
  horizontalPosition: PropTypes.oneOf(['left', 'center', 'right']),
  verticalPosition: PropTypes.oneOf(['top', 'bottom']),
};

SnackbarAlert.defaultProps = {
  variant: 'success',
  autoHideDuration: 2000,
  allowClose: true,
  horizontalPosition: 'center',
  verticalPosition: 'bottom',
};

export default SnackbarAlert;
